/**
 * Formatting. One place, so a count, a date and a percentage look the same
 * everywhere in the console.
 */

const numberFormat = new Intl.NumberFormat(undefined)
const compactFormat = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 })
const moneyFormat = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})
const moneyWholeFormat = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

export function formatNumber(value: number | null | undefined, fallback = '0'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  return numberFormat.format(value)
}

/** 12.4k for dense tiles. Anything under 10,000 stays exact so counts stay readable. */
export function formatCompactNumber(value: number | null | undefined, fallback = '0'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  if (Math.abs(value) < 10_000) return numberFormat.format(value)
  return compactFormat.format(value)
}

export function formatMoney(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  return Number.isInteger(value) && Math.abs(value) >= 1000
    ? moneyWholeFormat.format(value)
    : moneyFormat.format(value)
}

/**
 * Dollars to the dollar: `$40`, `$1,200`. For a budget or an allowance, which
 * is read to the dollar, where cents are noise.
 */
export function formatMoneyWhole(value: number | null | undefined, fallback = '$0'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  return moneyWholeFormat.format(Math.round(value))
}

/** `formatPercent(0.42)` is "42%". Pass `fromFraction: false` for a 0 to 100 input. */
export function formatPercent(
  value: number | null | undefined,
  options: { digits?: number; fromFraction?: boolean; fallback?: string } = {},
): string {
  const { digits = 0, fromFraction = true, fallback = '—' } = options
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  const pct = fromFraction ? value * 100 : value
  return `${pct.toFixed(digits)}%`
}

export function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const dateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
const dateShortFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })
const timeFormat = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' })
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})
const fullFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'short' })
const zonedDateTimeFormat = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
})

export function formatDate(value: Date | string | number | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  return date ? dateFormat.format(date) : fallback
}

export function formatDateShort(value: Date | string | number | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  return date ? dateShortFormat.format(date) : fallback
}

export function formatTime(value: Date | string | number | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  return date ? timeFormat.format(date) : fallback
}

/*
 * Salesforge's table timestamp, "Sep 21, 2026, 12:15 AM": month first, the
 * year always, the time in twelve hours. Every date column in a rebuilt table
 * uses this, so the tables read the same as the reference whatever the
 * browser's own locale would have chosen.
 */
const stampFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

export function formatStamp(value: Date | string | number | null | undefined, fallback = '-'): string {
  const date = toDate(value)
  return date ? stampFormat.format(date) : fallback
}

export function formatDateTime(value: Date | string | number | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  return date ? dateTimeFormat.format(date) : fallback
}

/**
 * A moment on the reader's own clock with the zone named, for a sentence that
 * promises a time. The phones keep their own clocks and the server keeps UTC, so
 * a bare "14:30" in a refusal is a guess about which of the three it means.
 */
export function formatLocalDateTime(value: Date | string | number | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  return date ? zonedDateTimeFormat.format(date) : fallback
}

/** The long form used in tooltips, where precision beats brevity. */
export function formatAbsolute(value: Date | string | number | null | undefined, fallback = ''): string {
  const date = toDate(value)
  return date ? fullFormat.format(date) : fallback
}

const relativeFormat = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

const RELATIVE_STEPS: { limitMs: number; divisorMs: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { limitMs: 60_000, divisorMs: 1_000, unit: 'second' },
  { limitMs: 3_600_000, divisorMs: 60_000, unit: 'minute' },
  { limitMs: 86_400_000, divisorMs: 3_600_000, unit: 'hour' },
  { limitMs: 604_800_000, divisorMs: 86_400_000, unit: 'day' },
  { limitMs: 2_629_800_000, divisorMs: 604_800_000, unit: 'week' },
  { limitMs: 31_557_600_000, divisorMs: 2_629_800_000, unit: 'month' },
  { limitMs: Number.POSITIVE_INFINITY, divisorMs: 31_557_600_000, unit: 'year' },
]

/** "3 minutes ago" or "in 2 hours". Past and future both read naturally. */
export function relativeTimeFrom(
  value: Date | string | number | null | undefined,
  now: Date = new Date(),
): string {
  const date = toDate(value)
  if (!date) return '—'
  const deltaMs = date.getTime() - now.getTime()
  const absMs = Math.abs(deltaMs)
  if (absMs < 15_000) return 'just now'
  const step = RELATIVE_STEPS.find((s) => absMs < s.limitMs) ?? RELATIVE_STEPS[RELATIVE_STEPS.length - 1]
  return relativeFormat.format(Math.round(deltaMs / step.divisorMs), step.unit)
}

/** "2 hours 30 minutes" from a minute count. Used for gaps and holds. */
export function formatDurationMinutes(minutes: number | null | undefined, fallback = '—'): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return fallback
  if (minutes < 1) return 'under a minute'
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = Math.round(minutes % 60)
  const parts: string[] = []
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)
  if (mins > 0 && days === 0) parts.push(`${mins} ${mins === 1 ? 'minute' : 'minutes'}`)
  return parts.join(' ') || 'under a minute'
}

/** "12 Aug, 09:30 (in 4 hours)" for anything a person has to wait for. */
export function formatWhen(
  value: Date | string | number | null | undefined,
  fallback = 'an unknown time',
): string {
  const date = toDate(value)
  if (!date) return fallback
  return `${formatDateTime(date)} (${relativeTimeFrom(date)})`
}

/** The word alone. `plural` is the one that puts the count in front of it. */
export function pluralize(count: number, one: string, many = `${one}s`): string {
  return count === 1 ? one : many
}

/** Cheap pluraliser for copy like "3 cards" and "1 card". */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${formatNumber(count)} ${pluralize(count, one, many)}`
}

/**
 * The ICP score is a 0 to 1 fraction in the data model and a 0 to 100 figure in
 * conversation, so it is shown the way people say it.
 */
export function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined || Number.isNaN(score)) return 'not scored'
  return String(Math.round(score <= 1 ? score * 100 : score))
}

/** The same score as a 0 to 1 fraction, for a bar. */
export function scoreFraction(score: number | null | undefined): number {
  if (score === null || score === undefined || Number.isNaN(score)) return 0
  return Math.max(0, Math.min(1, score <= 1 ? score : score / 100))
}

/** A wait of 36 hours reads better as "1 day 12 hours" than "36h". */
export function formatWait(
  wait: { days?: number; hours?: number; minutes?: number } | null | undefined,
): string {
  if (!wait) return 'no wait'
  const parts: string[] = []
  if (wait.days) parts.push(plural(wait.days, 'day'))
  if (wait.hours) parts.push(plural(wait.hours, 'hour'))
  if (wait.minutes) parts.push(plural(wait.minutes, 'minute'))
  return parts.length ? `wait ${parts.join(' ')}` : 'no wait'
}

/** Trim to a length without cutting a word in half. */
export function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

/**
 * Two-letter monogram for an avatar chip.
 *
 * Built from letters and digits only. A bracketed aside is left out and so is
 * punctuation, because "Device Cycle (temporary)" used to come out as "D(", and
 * a chip that prints a bracket reads as a rendering fault.
 */
export function initials(name: string | null | undefined): string {
  const words = (name ?? '')
    .replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
}

/** A LinkedIn or company URL shown without the noise. */
export function prettyUrl(url: string | null | undefined, fallback = '—'): string {
  if (!url) return fallback
  try {
    const parsed = new URL(url)
    const path = parsed.pathname.replace(/\/$/, '')
    return `${parsed.hostname.replace(/^www\./, '')}${path}`
  } catch {
    return url
  }
}

/** Whole percentage of a ceiling, clamped, for meters and health colouring. */
export function ratio(used: number, cap: number): number {
  if (!Number.isFinite(cap) || cap <= 0) return 0
  return Math.max(0, Math.min(1, used / cap))
}
